import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';

/* ──────────────────────────────────────────────
   Service checks
────────────────────────────────────────────── */
async function checkSupabaseDB() {
  const start = performance.now();
  try {
    const url = process.env.REACT_APP_SUPABASE_URL;
    const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const token = (() => {
      try {
        const s = sessionStorage.getItem('supabase-auth-token');
        return s ? (JSON.parse(s).access_token ?? key) : key;
      } catch { return key; }
    })();

    const res = await fetch(`${url}/rest/v1/profiles?select=id&limit=1`, {
      headers: { apikey: key, Authorization: `Bearer ${token}` },
    });

    const latency = Math.round(performance.now() - start);
    return { status: res.ok ? 'healthy' : 'degraded', latency };
  } catch {
    return { status: 'down', latency: null };
  }
}

async function checkAuth() {
  const start = performance.now();
  try {
    const url = process.env.REACT_APP_SUPABASE_URL;
    const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const res = await fetch(`${url}/auth/v1/settings`, {
      headers: { apikey: key },
    });
    const latency = Math.round(performance.now() - start);
    return { status: res.ok ? 'healthy' : 'degraded', latency };
  } catch {
    return { status: 'down', latency: null };
  }
}

async function checkStorage() {
  const start = performance.now();
  try {
    const url = process.env.REACT_APP_SUPABASE_URL;
    const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const res = await fetch(`${url}/storage/v1/bucket`, {
      headers: { apikey: key },
    });
    const latency = Math.round(performance.now() - start);
    /* 200 or 404 (no buckets) both mean storage is reachable */
    return { status: (res.ok || res.status === 404) ? 'healthy' : 'degraded', latency };
  } catch {
    return { status: 'down', latency: null };
  }
}

const SERVICE_CHECKS = [
  { key: 'db',      label: 'Database',          icon: 'bi-database-check',    check: checkSupabaseDB },
  { key: 'auth',    label: 'Authentication',     icon: 'bi-shield-check',      check: checkAuth },
  { key: 'storage', label: 'Storage Service',    icon: 'bi-hdd-network',       check: checkStorage },
  { key: 'client',  label: 'Client Application', icon: 'bi-display',           check: async () => ({ status: 'healthy', latency: 0 }) },
];

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const map = {
    healthy:  { cls: 'adm-badge--green',  dot: 'adm-health-dot--healthy',  label: 'Healthy'  },
    degraded: { cls: 'adm-badge--yellow', dot: 'adm-health-dot--degraded', label: 'Degraded' },
    down:     { cls: 'adm-badge--red',    dot: 'adm-health-dot--down',     label: 'Down'     },
    checking: { cls: 'adm-badge--gray',   dot: '',                          label: 'Checking…'},
  };
  const m = map[status] ?? map.checking;
  return (
    <span className={`adm-badge ${m.cls}`}>
      <span className={`adm-health-dot ${m.dot}`} style={status === 'checking' ? {} : undefined} />
      {m.label}
    </span>
  );
}

function OverallBanner({ statuses }) {
  const values = Object.values(statuses);
  if (values.length === 0 || values.some(s => s === 'checking')) return null;
  const allHealthy  = values.every(s => s === 'healthy');
  const anyDown     = values.some(s => s === 'down');

  if (allHealthy) return (
    <div className="adm-alert adm-alert--success">
      <i className="bi bi-check-circle-fill" />
      <strong>All Systems Operational.</strong> Every platform service is responding normally.
    </div>
  );
  if (anyDown) return (
    <div className="adm-alert adm-alert--danger">
      <i className="bi bi-exclamation-octagon-fill" />
      <strong>Service Disruption Detected.</strong> One or more services are unreachable. Investigate immediately.
    </div>
  );
  return (
    <div className="adm-alert adm-alert--warning">
      <i className="bi bi-exclamation-triangle-fill" />
      <strong>Partial Degradation.</strong> Some services are performing below normal. Monitor closely.
    </div>
  );
}

function getToken() {
  try {
    const s = sessionStorage.getItem('supabase-auth-token');
    return s ? (JSON.parse(s).access_token ?? process.env.REACT_APP_SUPABASE_ANON_KEY) : process.env.REACT_APP_SUPABASE_ANON_KEY;
  } catch { return process.env.REACT_APP_SUPABASE_ANON_KEY; }
}

/* ──────────────────────────────────────────────
   Main Component
────────────────────────────────────────────── */
const OrgHealth = () => {
  const [serviceResults, setServiceResults] = useState(
    Object.fromEntries(SERVICE_CHECKS.map(s => [s.key, { status: 'checking', latency: null }]))
  );
  const [tenantSummary, setTenantSummary] = useState({ total: null, active: null, issues: 0 });
  const [lastChecked, setLastChecked]     = useState(null);
  const [running, setRunning]             = useState(false);

  const runChecks = useCallback(async () => {
    setRunning(true);
    setServiceResults(Object.fromEntries(SERVICE_CHECKS.map(s => [s.key, { status: 'checking', latency: null }])));

    const results = await Promise.allSettled(SERVICE_CHECKS.map(s => s.check()));
    const updated = {};
    SERVICE_CHECKS.forEach((s, i) => {
      const r = results[i];
      updated[s.key] = r.status === 'fulfilled' ? r.value : { status: 'down', latency: null };
    });
    setServiceResults(updated);
    setLastChecked(new Date());

    /* Also pull tenant summary */
    try {
      const url = process.env.REACT_APP_SUPABASE_URL;
      const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const res = await fetch(`${url}/rest/v1/organizations?select=id,status`, {
        headers: { apikey: key, Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTenantSummary({
          total:  data.length,
          active: data.filter(o => o.status !== 'inactive').length,
          issues: data.filter(o => o.status === 'suspended' || o.status === 'past_due').length,
        });
      }
    } catch { /* Silently skip if table doesn't exist */ }

    setRunning(false);
  }, []);

  useEffect(() => { runChecks(); }, [runChecks]);

  const overallStatuses = Object.fromEntries(
    Object.entries(serviceResults).map(([k, v]) => [k, v.status])
  );

  const avgLatency = (() => {
    const vals = Object.values(serviceResults)
      .map(r => r.latency)
      .filter(l => l !== null && l !== undefined);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  })();

  return (
    <div className="adm-container">

      <div className="adm-page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 className="adm-page-title"><i className="bi bi-activity" />Org Health</h1>
          <p className="adm-page-subtitle">Real-time health overview of all platform services and tenant organizations.</p>
        </div>
        <button className="adm-btn adm-btn--outline" onClick={runChecks} disabled={running} style={{ flexShrink: 0, marginTop: 4 }}>
          <i className={`bi bi-arrow-clockwise${running ? ' adm-spin' : ''}`} style={{ display: 'inline-block', animation: running ? 'adm-spin 0.7s linear infinite' : 'none' }} />
          {running ? 'Checking…' : 'Refresh'}
        </button>
      </div>
      <hr className="adm-divider" />

      <div className="adm-stats-row">
        {[
          { label: 'Services Healthy', value: Object.values(serviceResults).filter(r => r.status === 'healthy').length + ' / ' + SERVICE_CHECKS.length, icon: 'bi-check-circle', color: '#006B54' },
          { label: 'Avg API Latency',  value: avgLatency !== null ? `${avgLatency}ms` : '—',  icon: 'bi-speedometer', color: '#0891b2' },
          { label: 'Active Tenants',   value: tenantSummary.active ?? '—',                    icon: 'bi-buildings',   color: '#1d4ed8' },
          { label: 'Tenants at Risk',  value: tenantSummary.issues ?? '—',                    icon: 'bi-exclamation-triangle', color: tenantSummary.issues > 0 ? '#d97706' : '#64748b' },
        ].map(s => (
          <div key={s.label} className="adm-stat-card" style={{ borderTopColor: s.color }}>
            <div className="adm-stat-icon" style={{ background: s.color + '18', color: s.color }}>
              <i className={`bi ${s.icon}`} />
            </div>
            <div className="adm-stat-body">
              <div className="adm-stat-value">{s.value}</div>
              <div className="adm-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <OverallBanner statuses={overallStatuses} />

      <div className="adm-grid-2">
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">Service Status</span>
            {lastChecked && (
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                Last checked {lastChecked.toLocaleTimeString()}
              </span>
            )}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {SERVICE_CHECKS.map((s, i) => {
              const r = serviceResults[s.key];
              return (
                <li
                  key={s.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '14px 20px',
                    borderBottom: i < SERVICE_CHECKS.length - 1 ? '1px solid #f1f5f4' : 'none',
                    gap: 12,
                  }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(0,107,84,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`bi ${s.icon}`} style={{ color: '#006B54', fontSize: '1rem' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#15211d' }}>{s.label}</div>
                    {r.latency !== null && r.status !== 'checking' && (
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>{r.latency}ms</div>
                    )}
                  </div>
                  <StatusBadge status={r.status} />
                </li>
              );
            })}
          </ul>
        </div>

        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">Tenants at Risk</span>
          </div>
          <div className="adm-card-body">
            {tenantSummary.issues === 0 ? (
              <div className="adm-empty" style={{ padding: 40 }}>
                <i className="bi bi-buildings" style={{ color: '#d1dbd7', fontSize: '2.5rem' }} />
                <span className="adm-empty-text">No at-risk tenants detected.</span>
                <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>All organizations are operating normally.</p>
              </div>
            ) : (
              <div className="adm-alert adm-alert--warning">
                <i className="bi bi-exclamation-triangle-fill" />
                {tenantSummary.issues} tenant{tenantSummary.issues !== 1 ? 's are' : ' is'} flagged with billing or status issues.
                <button className="adm-btn adm-btn--outline adm-btn--sm" style={{ marginLeft: 'auto' }} onClick={() => window.location.href = '/admin/billing'}>
                  View Billing
                </button>
              </div>
            )}

            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#4a5a54', marginBottom: 12 }}>Platform Snapshot</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'Total Tenants',  value: tenantSummary.total ?? '—' },
                  { label: 'Active Tenants', value: tenantSummary.active ?? '—' },
                  { label: 'At Risk',        value: tenantSummary.issues ?? '—', highlight: tenantSummary.issues > 0 },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8faf9', borderRadius: 8 }}>
                    <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{item.label}</span>
                    <span style={{ fontWeight: 700, color: item.highlight ? '#d97706' : '#15211d', fontSize: '0.9rem' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default OrgHealth;
